import type { Marker } from "@googlemaps/markerclusterer";
import type { PointOfInterest } from "../../../@types/mapviewxs";
import { unwatchMarker, watchMarker } from "../marker";
import type { MarkerWatchState, MarkerWithCircle, WatchListEntries } from "../types";
import { createClusterItems } from "../cluster";

/**
 * Removes the distance string from a POI's card so it doesn't display
 * in watchlist view (distance is irrelevant for watchlist items).
 */
const removeDistanceFromPOI = (poi: PointOfInterest): PointOfInterest => {
  if (!poi?.itemCard?.card?.distance) return poi;
  const { distance, ...cardWithoutDistance } = poi.itemCard.card;
  return {
    ...poi,
    itemCard: {
      ...poi.itemCard,
      card: cardWithoutDistance,
    },
  };
};

export const getWatchListItemState = (watchListItems: WatchListEntries, listingId?: string): MarkerWatchState => {
  if (!listingId || !watchListItems?.length) {
    return "unwatched";
  }

  return watchListItems.some(({ item }) => item?.listing?.listingId === listingId) ? "watched" : "unwatched";
};

export const toggleWatchlistItems = ({
  itemId,
  pointsOfInterest,
  points,
  watchListItems,
  isWatchlistPillActive,
  clearMarker,
}: {
  itemId: string;
  pointsOfInterest: PointOfInterest[];
  points: MarkerWithCircle[];
  watchListItems: WatchListEntries;
  isWatchlistPillActive: boolean;
  clearMarker: (index: number) => void;
}): WatchListEntries => {
  let newWatchlistItems = [];
  const indexItemInPoints = pointsOfInterest.findIndex(({ itemCard }) => itemCard?.card?.listingId === itemId);
  const marker = points[indexItemInPoints];
  const isItemInWatchList = watchListItems?.some(({ item }) => item?.listing?.listingId === itemId);
  if (isItemInWatchList) {
    newWatchlistItems = watchListItems?.filter(({ item }) => item?.listing?.listingId !== itemId);
    marker && unwatchMarker(marker);
    if (isWatchlistPillActive) {
      clearMarker(indexItemInPoints);
    }
  } else {
    newWatchlistItems = [
      ...watchListItems,
      {
        item: {
          listing: {
            listingId: itemId,
            poi: removeDistanceFromPOI(pointsOfInterest[indexItemInPoints]),
          },
        },
      },
    ];
    marker && watchMarker(marker);
  }
  return newWatchlistItems;
};

export const enrichWatchlistItems = ({
  watchlistItems,
  watchlistPointsOfInterest,
}: {
  watchlistItems: WatchListEntries;
  watchlistPointsOfInterest?: PointOfInterest[];
}): WatchListEntries => {
  return watchlistItems.reduce(
    (acc, { item }) => {
      const watchlistPOI = watchlistPointsOfInterest?.find(({ itemCard }) => itemCard?.card?.listingId === item.listing.listingId);
      if (watchlistPOI) {
        acc.push({
          item: {
            listing: {
              listingId: item.listing.listingId,
              poi: removeDistanceFromPOI(watchlistPOI),
            },
          },
        });
      }
      return acc;
    },
    [] as Exclude<WatchListEntries, []>,
  );
};

export const getWatchlistClusterItems = ({
  clusterMarkers,
  pointsOfInterest,
  watchListItems,
}: {
  clusterMarkers: Marker[];
  pointsOfInterest: PointOfInterest[];
  watchListItems: WatchListEntries;
}): PointOfInterest[] =>
  createClusterItems(clusterMarkers, pointsOfInterest).filter(({ itemCard }) =>
    watchListItems.find(({ item }) => item.listing.listingId === itemCard?.card.listingId),
  );
